import { useLang } from "@/contexts/LanguageContext";
import { Instagram, Mail, LifeBuoy, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const INSTAGRAM_HANDLE = "use.obra";
const INSTAGRAM_URL = "https://instagram.com/use.obra";
const SUPPORT_EMAIL = "support@useobra.org";

export default function SupportPage() {
  const { t } = useLang();

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      toast.success(t("email_copied"));
    } catch {
      toast.error("Clipboard error");
    }
  };

  return (
    <div className="space-y-8" data-testid="support-page">
      <div>
        <p className="label-eyebrow flex items-center">
          <span className="gold-rule" />
          {t("support")}
        </p>
        <h1 className="mt-3 font-[Fraunces] text-4xl sm:text-5xl font-bold text-neutral-950">
          {t("support_title")}
        </h1>
        <p className="mt-3 text-neutral-600 max-w-xl">{t("support_sub")}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Email */}
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="surface-card p-6 group hover:border-[#B8933A] transition-colors block"
          data-testid="support-email-card"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-[#FAF6EB] border border-[#B8933A]/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#B8933A]" strokeWidth={1.75} />
              </div>
              <div>
                <p className="label-eyebrow">{t("email_us")}</p>
                <p className="font-[Fraunces] text-xl text-neutral-950 mt-0.5">
                  {SUPPORT_EMAIL}
                </p>
              </div>
            </div>
            <ArrowUpRight className="w-4 h-4 text-neutral-400 group-hover:text-[#B8933A] transition-colors" />
          </div>
          <p className="mt-4 text-sm text-neutral-500">{t("support_email_desc")}</p>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); copyEmail(); }}
            className="mt-3 text-xs text-[#B8933A] font-medium hover:text-[#8C6D22]"
            data-testid="support-copy-email"
          >
            {t("copy_email")}
          </button>
        </a>

        {/* Instagram */}
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="surface-card p-6 group hover:border-[#B8933A] transition-colors block"
          data-testid="support-instagram-card"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-[#FAF6EB] border border-[#B8933A]/20 flex items-center justify-center">
                <Instagram className="w-5 h-5 text-[#B8933A]" strokeWidth={1.75} />
              </div>
              <div>
                <p className="label-eyebrow">{t("follow_us")}</p>
                <p className="font-[Fraunces] text-xl text-neutral-950 mt-0.5">
                  @{INSTAGRAM_HANDLE}
                </p>
              </div>
            </div>
            <ArrowUpRight className="w-4 h-4 text-neutral-400 group-hover:text-[#B8933A] transition-colors" />
          </div>
          <p className="mt-4 text-sm text-neutral-500">{t("support_instagram_desc")}</p>
        </a>
      </div>

      <div className="surface-card p-6 flex items-start gap-3">
        <LifeBuoy className="w-5 h-5 text-[#B8933A] mt-0.5 shrink-0" strokeWidth={1.75} />
        <div>
          <p className="font-[Fraunces] text-lg text-neutral-950">{t("support_response_title")}</p>
          <p className="text-sm text-neutral-600 mt-1">{t("support_response_desc")}</p>
        </div>
      </div>
    </div>
  );
}
